import React, { useState, useEffect } from 'react'

export default function OfferModal({ open, onClose }){
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' })
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')
  const [show, setShow] = useState(false)

  // Wait a tick to trigger entrance animations
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => setShow(true))
    } else {
      setShow(false)
    }
  }, [open])

  if(!open) return null

  const onChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const handleClose = () => {
    setShow(false)
    setTimeout(onClose, 300) // wait for exit animation
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    setError('')
    if(!form.name || !form.email || !form.message){
      setError('Please fill all required fields.')
      return
    }
    setSubmitting(true)
    try{
      const endpoint = 'https://docs.google.com/forms/d/e/1FAIpQLSdUpJPnt6LDqIdrKSu-0u4-KF_Dz1GnSrHYZgE5g5vJ6k482Q/formResponse'
      const data = new FormData()
      data.append('entry.2005620554', form.name)
      data.append('entry.1045781291', form.email)
      data.append('entry.839337160', `[${form.subject || 'Popup Contact'}] ${form.message}`)
      await fetch(endpoint, { method: 'POST', mode: 'no-cors', body: data })
      setSubmitted(true)
      // GA4 Conversion Event: track lead generation
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'generate_lead', {
          event_category: 'Lead',
          event_label: form.subject || 'Popup Contact',
          value: 1
        })
      }
      setForm({ name: '', email: '', subject: '', message: '' })
      setTimeout(handleClose, 2500)
    }catch(err){
      setError('Something went wrong. Please try again.')
    }finally{
      setSubmitting(false)
    }
  }

  const subjectLabels = {
    'Free Consultation'    : '💡 Free Consultation',
    'Get a Quote'          : '💼 Get a Quote',
    'Book a Session'       : '📅 Book a Session',
    'General Enquiry'      : '💬 General Enquiry',
  }

  return (
    <div className="fixed inset-0 z-[500] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className={`absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity duration-300 ${show ? 'opacity-100' : 'opacity-0'}`}
        onClick={handleClose} 
      />

      {/* Lightweight, animated glowing modal card */}
      <div 
        className={`relative w-full max-w-[440px] rounded-[24px] shadow-2xl transition-all duration-500 ease-out 
          ${show ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-95'}`}
      >
        {/* Animated glowing border effect */}
        <div className="absolute -inset-[2px] rounded-[26px] bg-gradient-to-r from-[#00cfff] via-[#4b00cc] to-[#00cfff] opacity-50 blur-md animate-pulse" />
        
        {/* Inner Card content */}
        <div className="relative bg-white rounded-[24px] overflow-hidden flex flex-col">
          
          {/* Header Theme Banner */}
          <div className="relative pt-8 pb-6 px-8 text-center bg-gradient-to-br from-[#0b3b8f] via-[#1a0099] to-[#4b00cc]">
            {/* Close Button */}
            <button
              onClick={handleClose}
              aria-label="Close modal"
              className="absolute right-3 top-3 h-8 w-8 rounded-full bg-white/10 text-white/70 flex items-center justify-center hover:bg-white/20 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>

            {/* Logo box with light background */}
            <div className={`mx-auto w-fit bg-white rounded-2xl px-6 py-3 shadow-[0_8px_30px_rgba(0,0,0,0.5)] flex items-center justify-center mb-5 transform transition-all duration-700 delay-100 ${show ? 'scale-100 translate-y-0 opacity-100' : 'scale-75 translate-y-4 opacity-0'}`}>
              <img src="/icons/header-logo.webp" alt="NexGenium" width="160" height="48" className="h-10 md:h-12 lg:h-14 object-contain" />
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-1">Let's Connect</h2>
            <p className="text-[#00cfff] text-sm">Empowering your digital future.</p>
          </div>

          {/* Form Area */}
          <div className="p-8">
            {submitted && !error ? (
              <div className="text-center py-6 animate-fade-in">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-2">Message Sent!</h3>
                <p className="text-slate-500 text-sm">We'll be in touch with you shortly.</p>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4">
                {/* Inputs with staggered slide-up animation */}
                <div className={`transform transition-all duration-500 delay-100 ${show ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
                  <input
                    type="text" name="name" value={form.name} onChange={onChange} required
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#0b3b8f]/40 transition-all"
                    placeholder="Your Name *"
                  />
                </div>
                <div className={`transform transition-all duration-500 delay-150 ${show ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
                  <input
                    type="email" name="email" value={form.email} onChange={onChange} required
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#0b3b8f]/40 transition-all"
                    placeholder="Email Address *"
                  />
                </div>

                <div className={`transform transition-all duration-500 delay-200 ${show ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
                  <label htmlFor="offer-subject" className="sr-only">How can we help?</label>
                  <select
                    id="offer-subject"
                    name="subject" value={form.subject} onChange={onChange}
                    suppressHydrationWarning
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#0b3b8f]/40 transition-all appearance-none cursor-pointer"
                  >
                    <option value="" suppressHydrationWarning>— How can we help? —</option>
                    {Object.entries(subjectLabels).map(([val, label]) => (
                      <option key={val} value={val}>{label}</option>
                    ))}
                  </select>
                </div>

                <div className={`transform transition-all duration-500 delay-250 ${show ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
                  <textarea
                    name="message" value={form.message} onChange={onChange} required rows={3}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#0b3b8f]/40 transition-all resize-none"
                    placeholder="Briefly describe your requirements..."
                  />
                </div>

                {error && <div className="text-sm text-red-500 text-center">{error}</div>}

                <div className={`pt-2 transform transition-all duration-500 delay-300 ${show ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
                  <button
                    type="submit" disabled={submitting}
                    className="relative w-full overflow-hidden rounded-xl bg-gradient-to-r from-[#0b3b8f] to-[#4b00cc] p-[2px] group"
                  >
                    <span className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
                    <div className="relative flex items-center justify-center bg-gradient-to-r from-[#0b3b8f] to-[#1a0099] rounded-[10px] px-6 py-3 text-white text-base font-bold shadow-[inset_0_1px_rgba(255,255,255,0.2)] transition-transform md:group-hover:scale-[0.98]">
                      {submitting ? 'Transmitting Data...' : 'Submit'}
                    </div>
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
